import { Component, computed, inject, OnDestroy, OnInit, signal } from "@angular/core"
import { IntroService } from "./intro.service"
import { HeaderLogoComponent } from "./header/header-logo.component"
import { HeaderBrandComponent } from "./header/header-brand.component"
import { LegacyComponent } from "./legacy/legacy.component"
import { GradesComponent } from "./grades/grades.component"
import { IntroVirtualTourCtaComponent } from "./intro-virtual-tour-cta.component"
import { DefinitionsComponent } from "./definitions/definitions.component"
import { PageToolbarService } from "../../page-toolbar.service"
import { UiInheritDirective } from "ui-inherit"
import { BannerComponent } from "./banner/banner.component"
import { NgOptimizedImage } from "@angular/common"
import { UnderwritingManualComponent } from "./underwriting-manual/underwriting-manual.component"
import { RouterLink } from "@angular/router"
import { regionsData } from "redlineLibs"
import { RouteService } from "../../../services/route.service"
import { StorageService } from "../../../services/storage.service"
import { prefersReducedMotionQuery } from "ui-theme"

@Component({
  imports: [
    HeaderLogoComponent,
    HeaderBrandComponent,
    LegacyComponent,
    GradesComponent,
    IntroVirtualTourCtaComponent,
    DefinitionsComponent,
    UiInheritDirective,
    BannerComponent,
    NgOptimizedImage,
    UnderwritingManualComponent,
    RouterLink,
  ],
  providers: [],
  standalone: true,
  styles: `
    @keyframes fade-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes fade-out {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }

    .banner-image {
      transition: opacity 1200ms ease-in-out;
    }

    .banner-image-active {
      opacity: 1;
    }

    .banner-image-inactive {
      opacity: 0;
    }

    .fade-in {
      animation: fade-in 4s ease-in-out;
    }

    .fade-out {
      animation: fade-out 4s ease-in-out;
    }
  `,
  template: `
    <div
      #pageToolbarOffset
      [style.height.px]="pageToolbarHeight()"
      class="w-full"
    ></div>
    <div class="column bg-base-100 relative">
      <div class="pl-10">
        <div class="border-l-8 border-dashed border-primary">
          <div class="row gap-20 p-10">
            <div class="column gap-5">
              @for (introLogo of logos(); track index; let index = $index) {
                <e2e-intro-header-logo [introLogo]="introLogo"/>
              }
            </div>
            <e2e-header-brand inherit/>
          </div>
        </div>
      </div>


      <div class="pl-10 relative">
        <!-- Background image container with cross-fading images -->
        <div class="absolute inset-0 bg-accent overflow-hidden">
          @for (image of bannerBackgroundImages(); track image; let i = $index) {
            <img
              [ngSrc]="image"
              [alt]="'Banner background image ' + (i + 1)"
              class="banner-image absolute inset-0 w-full h-full object-cover object-center"
              fill
              [class.banner-image-active]="i === currentBannerBackgroundImageIndex()"
              [class.banner-image-inactive]="i !== currentBannerBackgroundImageIndex()"
              [priority]="i === 0"
            >
          }
        </div>

        <!-- Content on top of background -->
        <div class="relative">
          <e2e-banner/>
          @if (audioTour()) {
            <e2e-intro-virtual-tour-cta inherit/>
          }
        </div>

      </div>

      <div class="pl-10">
        <div class="border-l-8 border-dashed border-primary">
          <div class="p-10">
            <e2e-definitions inherit/>
          </div>
        </div>
      </div>

      <div class="pl-10 relative">
        <div class="column-center border-l-8 border-dashed border-primary">
          <div class="p-5 ">
            <div
              class="column items-center bg-base-100 rounded-lg px-5 py-8"
              data-theme="redline_dark"
            >
              <div>
                "Residential security maps" were created for 239 cities across the United States. These became the basis
                for explicitly racist disinvestment in communities of color. The example map below depicts the 1939
                redlining plan in part of the Bronx (New York City), a community that continues to face the long-term
                effects of redlining.
              </div>
              <div>
                Click below or on the map to learn how neighborhoods we classified into 4 categories:
              </div>
              <div class="row-center">
                <button
                  class="btn btn-green btn-xs"
                  aria-label="Grade A, color coded Green: Most Desirable"
                  [routerLink]="[]"
                  [fragment]="grades()[0].grade.toLowerCase()"
                  queryParamsHandling="preserve"
                >
                  Green - Grade A
                </button>
                <button
                  class="btn btn-blue btn-xs"
                  aria-label="Grade B, color coded Blue: Still Desirable"
                  [routerLink]="[]"
                  [fragment]="grades()[1].grade.toLowerCase()"
                  queryParamsHandling="preserve"
                >
                  Blue - Grade B
                </button>
                <button
                  class="btn btn-yellow btn-xs"
                  aria-label="Grade C, color coded Yellow: Definitely Declining"
                  [routerLink]="[]"
                  [fragment]="grades()[2].grade.toLowerCase()"
                  queryParamsHandling="preserve"
                >
                  Yellow - Grade C
                </button>
                <button
                  class="btn btn-red btn-xs"
                  aria-label="Grade D, color coded Red: Least Desirable"
                  [routerLink]="[]"
                  [fragment]="grades()[3].grade.toLowerCase()"
                  queryParamsHandling="preserve"
                >
                  Red - Grace D
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pl-10 relative overflow-hidden">
        <div class="absolute inset-0 bg-accent">
          <img
            [ngSrc]="bannerBackgroundImages()[1]"
            alt="Banner background"
            class="absolute inset-0 w-full h-[200%] object-cover object-center"
            fill
          >
        </div>

        <div class="relative">
          <e2e-grades/>
        </div>
      </div>

      <div class="pl-10">
        <div class="border-l-8 border-dashed border-primary">
          <e2e-underwriting-manual/>
        </div>
      </div>

      <div
        class="pl-10"
        style="background: bottom / cover no-repeat"
        [style.background-image]="backgroundImage()"
      >
        <div class="border-l-8 border-dashed border-primary">
          <e2e-legacy inherit/>
        </div>
      </div>

    </div>
  `,
  selector: "e2e-intro",
})
export class IntroComponent implements OnInit, OnDestroy {
  private introService = inject(IntroService)
  private pageToolbarService = inject(PageToolbarService)
  private routeService = inject(RouteService)
  private storageService = inject(StorageService)

  pageToolbarHeight = this.pageToolbarService.pageToolbarHeight
  logos = this.introService.logos

  backgroundImage = computed(() => {
    const url = this.storageService.getFileUrl("/assets/pages/intro/legacy/legacy-background-image.jpg")
    return "url('" + url + "')"
  })

  audioTour = computed(() =>
    regionsData[this.routeService.region()].audioTour
  )

  // Signal to track current image index
  currentBannerBackgroundImageIndex = signal(0)
  private intervalId: number | undefined

  bannerBackgroundImages = computed(() => {
    const bannerBackgroundImagesArray = [
      "couples.png",
      "men-march.png",
      "running-hydrant.png",
      "suited-men.png",
    ]
    return bannerBackgroundImagesArray.map(image => {
      const url = "/assets/pages/intro/banner/" + image
      return this.storageService.getFileUrl(url)
    })
  })

  currentBannerBackgroundImage = computed(() => {
    const images = this.bannerBackgroundImages()
    return images[this.currentBannerBackgroundImageIndex() % images.length]
  })

  grades = this.introService.grades

  ngOnInit() {
    // Start the image rotation timer (change image every n seconds)
    this.intervalId = window.setInterval(() => {
      !prefersReducedMotionQuery.matches &&
      this.currentBannerBackgroundImageIndex.update(index =>
        (index + 1) % this.bannerBackgroundImages().length
      )
    }, 8000)
  }

  ngOnDestroy() {
    // Clean up the interval when component is destroyed
    if (this.intervalId) {
      window.clearInterval(this.intervalId)
    }
  }
}
